import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WfTasksPagination from '@/pages/ipe/workbench/components/WfTasksPagination';
import '@/styles/ipe/workbench/IpeWorkflowTasksPage.css';

export const LIBRARY_MODAL_DEFAULT_PAGE_SIZE = 20;
export const LIBRARY_MODAL_DEFAULT_PAGE_SIZE_OPTIONS = [20, 50, 200];
export const WORKFLOW_LIBRARY_PAGE_SIZE_OPTIONS = [20, 50, 200];

function toLibraryPageSizeOptions(options, unitLabel, t) {
  return (Array.isArray(options) ? options : []).map((size) => ({
    value: size,
    label: t('libraryPagination.pageSizeOption', { size, unit: unitLabel }),
  }));
}

export function useLibraryModalPagination(
  items,
  { isActive = true, resetKey = '', defaultPageSize = LIBRARY_MODAL_DEFAULT_PAGE_SIZE } = {},
) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const totalItems = list.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize) || 1);

  useEffect(() => {
    if (!isActive) return;
    setPageSize(defaultPageSize ?? LIBRARY_MODAL_DEFAULT_PAGE_SIZE);
    setCurrentPage(1);
  }, [isActive, resetKey, defaultPageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return list.slice(start, start + pageSize);
  }, [list, currentPage, pageSize]);

  return {
    currentPage,
    pageSize,
    totalPages,
    totalItems,
    paginatedItems,
    setCurrentPage,
    setPageSize,
  };
}

export default function LibraryModalPagination({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = LIBRARY_MODAL_DEFAULT_PAGE_SIZE_OPTIONS,
  pageSizeUnitLabel,
  totalUnit,
  className = '',
  showTotalInfo = true,
  ariaLabel,
  /** 为 true 时即使 total=0 也展示分页条（便于切换每页条数） */
  showWhenEmpty = false,
}) {
  const { t } = useTranslation('workbench');
  if (!showWhenEmpty && totalItems === 0) return null;

  const wfPageSizeOptions = toLibraryPageSizeOptions(
    pageSizeOptions,
    pageSizeUnitLabel || t('libraryPagination.unitImages'),
    t,
  );

  const handlePageSizeChange = (nextSize) => {
    onPageSizeChange?.(nextSize);
    onPageChange?.(1);
  };

  return (
    <WfTasksPagination
      variant="element"
      className={`funding-library-modal__pagination${className ? ` ${className}` : ''}`}
      currentPage={currentPage}
      totalPages={totalPages}
      totalItems={totalItems}
      onPageChange={onPageChange}
      pageSize={pageSize}
      onPageSizeChange={handlePageSizeChange}
      pageSizeOptions={wfPageSizeOptions}
      showTotal={showTotalInfo}
      totalUnit={totalUnit || t('libraryPagination.totalRecords')}
      ariaLabel={ariaLabel || t('libraryPagination.ariaLabel')}
    />
  );
}
